import type { UserRole } from '../types/user';
import { getRoleViewOverride } from './roleViewOverride.js';

/** Canonical platform owner account — always super_admin on T&B and ServiceApp. */
export const PLATFORM_SUPER_ADMIN_EMAIL = 'febishg@gmail.com';

const STAFF_BOOTSTRAP_ROLES = new Set<UserRole>([
  'employee',
  'supervisor',
  'manager',
  'admin',
  'super_admin',
  'cleaner',
  'driver',
]);

export function isPlatformSuperAdminEmail(email: string | null | undefined): boolean {
  return (email ?? '').trim().toLowerCase() === PLATFORM_SUPER_ADMIN_EMAIL;
}

/**
 * Role to use before profiles load — avoids flashing the customer dashboard on refresh.
 * Platform owner lands on admin/super_admin immediately; staff use cached DB role when known.
 */
const PLATFORM_OWNER_BOOTSTRAP_ROLES = new Set<UserRole>(['super_admin', 'admin', 'supervisor', 'customer']);

export function resolveBootstrapDashboardRole(
  email: string | null | undefined,
  profileRole?: string | null,
  userId?: string | null,
): UserRole {
  if (isPlatformSuperAdminEmail(email)) {
    if (userId) {
      const override = getRoleViewOverride(userId);
      if (override?.targetRole && PLATFORM_OWNER_BOOTSTRAP_ROLES.has(override.targetRole as UserRole)) {
        return override.targetRole as UserRole;
      }
    }
    return resolvePlatformOwnerDashboardRole();
  }
  const normalized = (profileRole ?? '').trim().toLowerCase();
  if (STAFF_BOOTSTRAP_ROLES.has(normalized as UserRole)) {
    return normalized as UserRole;
  }
  return 'customer';
}

/** T&B day-to-day ops use Admin dashboard (enquiries, bookings); SaaS uses Platform Admin. */
export function resolvePlatformOwnerDashboardRole(): 'admin' | 'super_admin' {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (host === 'toolsandbubbles.com' || host.endsWith('.toolsandbubbles.com')) {
      return 'admin';
    }
    if (
      host === 'serviceapp.ai' ||
      host.endsWith('.serviceapp.ai') ||
      host.startsWith('serviceapp-platform')
    ) {
      return 'super_admin';
    }
  }
  const envMode = (import.meta.env && (import.meta.env.VITE_APP_MODE as string | undefined))?.trim();
  return envMode === 'saas-platform' ? 'super_admin' : 'admin';
}
